import { useState, useEffect } from 'react';
import { X, Folder, Image as ImageIcon, Loader, ChevronLeft, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DriveSelector({ onClose, onSelect, initialSelected = [], maxPhotos = 15 }) {
  const [folders, setFolders] = useState([]);
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentParentId, setCurrentParentId] = useState(null);
  const [folderHistory, setFolderHistory] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(initialSelected);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    // Filter folders based on search query
    if (searchQuery.trim() === '') {
      setFilteredFolders(folders);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredFolders(
        folders.filter(folder => 
          folder.name.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, folders]);

  const fetchFolders = async (parentId = null) => {
    setIsLoadingFolders(true);
    const token = localStorage.getItem('session_token');

    try {
      const url = parentId 
        ? `${API}/drive/folders?parent_id=${parentId}`
        : `${API}/drive/folders`;
        
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setFolders(response.data.folders);
      setFilteredFolders(response.data.folders);
      setCurrentParentId(parentId);
    } catch (error) {
      console.error('Error fetching folders:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar carpetas');
    } finally {
      setIsLoadingFolders(false);
    }
  };

  const navigateToFolder = (folderId, folderName) => {
    // Add current state to history
    if (currentParentId || folderHistory.length === 0) {
      setFolderHistory([...folderHistory, { id: currentParentId, name: folderName }]);
    }
    fetchFolders(folderId);
    setSearchQuery('');
  };

  const navigateBack = () => {
    if (folderHistory.length > 0) {
      const newHistory = [...folderHistory];
      const previous = newHistory.pop();
      setFolderHistory(newHistory);
      fetchFolders(previous.id);
      setSearchQuery('');
    }
  };

  const fetchFolderFiles = async (folderId) => {
    setIsLoadingFiles(true);
    const token = localStorage.getItem('session_token');

    try {
      const response = await axios.get(`${API}/drive/folder/${folderId}/files`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setFiles(response.data.files);
      setSelectedFolder(folderId);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Error al cargar archivos');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const toggleFileSelection = (file) => {
    const isSelected = selectedFiles.some(f => f.id === file.id);
    if (isSelected) {
      setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
      return;
    }
    if (file.exceeds_limit) {
      toast.error(`"${file.name}" supera 10MB (${file.size_mb}MB). No se puede seleccionar.`);
      return;
    }
    if (selectedFiles.length >= maxPhotos) {
      toast.error(`Máximo ${maxPhotos} fotos por proyecto`);
      return;
    }
    setSelectedFiles([...selectedFiles, file]);
  };

  const handleConfirm = () => {
    if (selectedFiles.length === 0) {
      toast.error('Selecciona al menos una foto');
      return;
    }
    onSelect(selectedFiles);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-neutral-900 border border-neutral-800 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-neutral-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl text-white font-medium">Seleccionar Fotos de Google Drive</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="close-drive-selector">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto grid lg:grid-cols-3 divide-x divide-neutral-800">
          {/* Folders */}
          <div className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm text-neutral-400">Carpetas</h3>
              {folderHistory.length > 0 && (
                <button
                  onClick={navigateBack}
                  className="flex items-center gap-1 text-xs text-neutral-500 hover:text-white"
                  data-testid="back-folder-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Volver
                </button>
              )}
            </div>
            
            {/* Search input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Buscar carpeta..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 bg-neutral-800 border border-neutral-700 text-white text-sm focus:border-white focus:outline-none"
                data-testid="folder-search-input"
              />
            </div>

            {/* Folders list with scroll */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ maxHeight: 'calc(70vh - 200px)' }}>
              {isLoadingFolders ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-6 h-6 text-neutral-400 animate-spin" />
                </div>
              ) : filteredFolders.length === 0 ? (
                <p className="text-sm text-neutral-500 py-12 text-center">
                  {searchQuery ? 'No se encontraron carpetas' : 'No hay carpetas disponibles'}
                </p>
              ) : (
                filteredFolders.map((folder) => (
                  <div
                    key={folder.id}
                    className={`flex items-center gap-2 border ${
                      selectedFolder === folder.id
                        ? 'bg-neutral-800 border-white'
                        : 'border-neutral-800 hover:border-neutral-600'
                    }`}
                  >
                    <button
                      onClick={() => fetchFolderFiles(folder.id)}
                      className="flex-1 flex items-center gap-3 px-4 py-3 text-left text-neutral-300"
                      data-testid={`folder-${folder.id}`}
                    >
                      <Folder className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm truncate">{folder.name}</span>
                    </button>
                    <button
                      onClick={() => navigateToFolder(folder.id, folder.name)}
                      className="px-3 py-3 text-neutral-500 hover:text-white border-l border-neutral-800"
                      title="Abrir sub-carpetas"
                      data-testid={`open-folder-${folder.id}`}
                    >
                      <FolderOpen className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
            
            {filteredFolders.length > 0 && (
              <p className="text-xs text-neutral-500 mt-3">
                {filteredFolders.length} carpeta(s) {searchQuery && 'encontrada(s)'}
              </p>
            )}
          </div>

          {/* Files */}
          <div className="lg:col-span-2 p-6">
            <h3 className="text-sm text-neutral-400 mb-4">
              Archivos <span className="text-neutral-600">(máx. 10MB c/u)</span> {selectedFiles.length > 0 && <span className="text-white">— {selectedFiles.length}/{maxPhotos} seleccionado(s)</span>}
            </h3>
            {!selectedFolder ? (
              <div className="flex items-center justify-center py-24 text-neutral-500">
                <div className="text-center space-y-2">
                  <Folder className="w-12 h-12 mx-auto opacity-50" />
                  <p className="text-sm">Selecciona una carpeta para ver las fotos</p>
                </div>
              </div>
            ) : isLoadingFiles ? (
              <div className="flex items-center justify-center py-24">
                <Loader className="w-6 h-6 text-neutral-400 animate-spin" />
              </div>
            ) : files.length === 0 ? (
              <div className="flex items-center justify-center py-24 text-neutral-500">
                <div className="text-center space-y-2">
                  <ImageIcon className="w-12 h-12 mx-auto opacity-50" />
                  <p className="text-sm">No hay imágenes en esta carpeta</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {files.map((file) => {
                  const isSelected = selectedFiles.some(f => f.id === file.id);
                  const tooBig = file.exceeds_limit;
                  return (
                    <button
                      key={file.id}
                      onClick={() => toggleFileSelection(file)}
                      disabled={tooBig && !isSelected}
                      className={`relative aspect-square overflow-hidden border-2 ${
                        isSelected
                          ? 'border-white'
                          : tooBig
                          ? 'border-red-900 opacity-50 cursor-not-allowed'
                          : 'border-transparent hover:border-neutral-600'
                      }`}
                      data-testid={`file-${file.id}`}
                      title={tooBig ? `Demasiado grande (${file.size_mb}MB)` : `${file.name} (${file.size_mb}MB)`}
                    >
                      <img
                        src={file.thumbnailLink || file.url}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      {file.size_mb > 0 && (
                        <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[10px] ${tooBig ? 'bg-red-600/90 text-white' : 'bg-black/70 text-white'}`}>
                          {file.size_mb}MB
                        </span>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-white/20 flex items-center justify-center">
                          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-800 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-neutral-700 text-neutral-300 hover:text-white hover:border-white"
            data-testid="cancel-drive-selection"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-6 py-3 bg-white text-black font-medium hover:bg-neutral-200"
            data-testid="confirm-drive-selection"
          >
            Confirmar Selección ({selectedFiles.length})
          </button>
        </div>
      </div>
    </div>
  );
}

export default DriveSelector;